/**
 * @file src/utils/logger.js
 * @class MoksyLogger
 * @description 
 * Authoritative Platform Logger for the Dashboard UI.
 * Enforces structured telemetry, traceId extraction, and error classification.
 * 
 * @actor System/Forensics
 * @action Log/Telemetry
 * @metric 12.1, 12.5, 18.2
 */
class MoksyLogger {
  /**
   * 📝 LOG
   * Logic: Central dispatcher for all telemetry levels.
   */
  static log(event, data = {}, level = 'info') {
    // RF-021: this is called from ErrorBoundary#componentDidCatch (via
    // .error()) to report a render crash. An unguarded throw HERE happens
    // inside componentDidCatch itself, which no error boundary can catch —
    // so a storage-blocked browser didn't just fail to show the fallback
    // screen, it took the whole React tree down to a blank page instead,
    // turning a recoverable crash into the thing the register walked into.
    let correlationId = 'unknown';
    try { correlationId = localStorage.getItem('last_trace_id') || 'unknown'; } catch { /* storage blocked */ }
    
    // 🛡️ Standard 12.5: Extract human-readable details from complex error objects
    let cleanData = { ...data };
    if (data instanceof Error) {
      cleanData = {
        message: data.message,
        stack: data.stack,
        // Check if it's a Zod Validation Error (Metric 3)
        isZodError: data.name === 'ZodError',
        zodIssues: data.issues || null,
        // Check if it's an Axios Network Error (Metric 13)
        isAxiosError: !!data.isAxiosError,
        status: data.response?.status,
        url: data.config?.url
      };
    }

    const payload = {
      timestamp: new Date().toISOString(),
      level,
      event,
      correlationId,
      ...cleanData,
      env: import.meta.env.MODE
    };

    /**
     * 🖥️ DX Output (Metric 18.2)
     * Logic: Provides styled, grouped console output during development.
     */
    if (import.meta.env.DEV) {
      const color = level === 'error' ? 'color: #ff4d4d' : 
                    level === 'warn'  ? 'color: #f39c12' : 'color: #ffa500';
      
      console.groupCollapsed(`%c[Moksy:${level.toUpperCase()}] ${event}`, color);
      console.log('Payload:', payload);
      if (cleanData.zodIssues) console.table(cleanData.zodIssues); 
      console.groupEnd();
    }

    // 📡 Future: Synchronize with logs.moksy.com (Service #32)
  }

  /**
   * ⚠️ WARN
   * Logic: High-visibility for recoverable anomalies.
   * ✅ FIXED: Added to prevent TypeError in useRequirements.js
   */
  static warn(event, data = {}) {
    this.log(event, data, 'warn');
  }

  /**
   * 🚨 ERROR
   * Logic: High-priority for system-breaking exceptions.
   */
  static error(event, error, context = {}) {
    this.log(event, error, 'error');
  }
}

export default MoksyLogger;